import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { cachedGet, invalidateCache } from '../../lib/api.js'
import { useMe } from '../../lib/me.js'
import { canViewSalary, isHR } from '../../lib/role.js'
import EmployeeEditor from './EmployeeEditor.jsx'

/**
 * ONE employee-detail drawer for the whole พนักงาน section.
 *
 * Every table under /manager/employee names a person — the clock-in summary, the
 * leave ledger, the หนังสือเตือน and ลาออก queues, the payroll ledger, the
 * evaluation report — and until now the only way to answer "who is this, which
 * branch, when did they start?" was to leave the page for รายชื่อ, find them again,
 * and lose the filters you had set. The row click on those pages is already spoken
 * for (it opens the warning, the request, the shift), so the person is opened from
 * a SECOND target inside the row: the avatar and the name.
 *
 * Mounted once in EmployeeLayout so no page has to wire up the editor, the branch
 * list and the two role gates itself; `useEmployeeDrawer()` reaches it from anywhere
 * below. The drawer IS EmployeeEditor — the same thing รายชื่อ opens, not a
 * read-only twin that would drift away from it.
 */
const Ctx = createContext(null)

/** `open(employeeId, onSaved?)` — null outside the provider, so callers must guard. */
export function useEmployeeDrawer() {
  return useContext(Ctx)
}

export function EmployeeDrawerProvider({ children }) {
  const me = useMe()
  const [open, setOpen] = useState(null) // { employeeId, onSaved }
  const [branches, setBranches] = useState([])

  // Fetched on the FIRST open, not on mount: most visits to these pages never open
  // a person, and /api/branches is memoized anyway (cachedGet) — on the pages that
  // already load it this resolves without a request.
  useEffect(() => {
    if (!open || branches.length) return
    let alive = true
    cachedGet('/api/branches')
      .then((r) => { if (alive) setBranches(Array.isArray(r) ? r : (r?.items || [])) })
      .catch(() => {})
    return () => { alive = false }
  }, [open, branches.length])

  const openEmployee = useCallback((employeeId, onSaved) => {
    const id = Number(employeeId)
    if (!id) return
    setOpen({ employeeId: id, onSaved })
  }, [])

  return (
    <Ctx.Provider value={openEmployee}>
      {children}
      {open && (
        <EmployeeEditor
          mode="edit"
          employeeId={open.employeeId}
          branches={branches}
          canSalary={canViewSalary(me)}
          isHrAdmin={isHR(me)}
          onClose={() => setOpen(null)}
          onSaved={() => {
            // The name/code just changed under every page that reads the roster from
            // the memoized GET — drop it so the next reader sees the edit rather than
            // the row it rendered ten minutes ago.
            invalidateCache('/api/hr/employees')
            open.onSaved?.()
            setOpen(null)
          }}
        />
      )}
    </Ctx.Provider>
  )
}

/**
 * The click target. Wraps an existing avatar + name cell — the markup stays exactly
 * as each page wrote it, this only adds the hit area around it.
 *
 * A <span role="button">, never a <button>: three of these cells live inside a
 * clickable <tr> and one inside a <button> card, and a nested <button> is invalid
 * HTML that React will not render the way you meant. `stopPropagation` is what keeps
 * the row's own click (open the warning / select the group) from firing too.
 *
 * Degrades to plain children when there is no id, or when it is used outside the
 * provider — a cell whose employee row never loaded must still render its text.
 */
export function EmployeeTrigger({ employeeId, onSaved, title = 'ดูข้อมูลพนักงาน', className = '', children }) {
  const openEmployee = useEmployeeDrawer()
  if (!openEmployee || !Number(employeeId)) return <>{children}</>
  const go = () => openEmployee(employeeId, onSaved)
  return (
    <span
      role="button"
      tabIndex={0}
      title={title}
      onClick={(e) => { e.stopPropagation(); go() }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return
        e.preventDefault(); e.stopPropagation(); go()
      }}
      className={`inline-flex min-w-0 max-w-full -mx-1 px-1 py-0.5 rounded-lg cursor-pointer transition hover:bg-brand-50 hover:ring-1 hover:ring-brand-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${className}`}
    >
      {children}
    </span>
  )
}
